import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '../../config/firebase';
import { InvitePreview, acceptInvite, previewInvite } from '../../api/tenantAdmin';
import './TenantInvitePage.css';

/**
 * Where an invited colleague of a partner joins.
 *
 * Public on purpose: the recipient has no account with us until this page
 * finishes. The invite token is the credential, and the backend still
 * insists that the Firebase account they end up signed in as owns the
 * address the invite was sent to, so forwarding the link to somebody else
 * does not transfer it.
 *
 * Note what this page does *not* do: it never sends a password anywhere
 * except Firebase. We create no accounts and store no credentials.
 */

type Mode = 'choose' | 'password';

const TenantInvitePage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState<Mode>('choose');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  useEffect(() => {
    let cancelled = false;
    previewInvite(token)
      .then(data => !cancelled && setInvite(data))
      .catch((e: any) =>
        !cancelled && setLoadError(e?.message || 'This invite is no longer valid.')
      );
    return () => {
      cancelled = true;
    };
  }, [token]);

  const finish = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    // Firebase caches claims; force a refresh so email_verified is current
    // for a user who verified seconds ago.
    const idToken = await user.getIdToken(true);
    await acceptInvite(token, idToken);
    setDone(true);
  }, [token]);

  const withErrors = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e: any) {
      if (e?.code === 'auth/email-already-in-use') {
        setError('You already have an account. Enter your existing password instead.');
      } else if (e?.code === 'auth/wrong-password' || e?.code === 'auth/invalid-credential') {
        setError('That password did not match.');
      } else if (e?.code === 'auth/weak-password') {
        setError('Choose a longer password, at least six characters.');
      } else {
        setError(e?.message || 'That did not work.');
      }
    } finally {
      setBusy(false);
    }
  };

  const joinWithGoogle = () =>
    withErrors(async () => {
      await signInWithPopup(auth, new GoogleAuthProvider());
      await finish();
    });

  const joinWithPassword = (e: React.FormEvent) => {
    e.preventDefault();
    withErrors(async () => {
      if (!invite) return;
      try {
        await signInWithEmailAndPassword(auth, invite.email, password);
      } catch (signInError: any) {
        // No account yet is the normal path for an invited person, so create
        // one rather than treating it as a failure.
        if (
          signInError?.code === 'auth/user-not-found' ||
          signInError?.code === 'auth/invalid-credential'
        ) {
          await createUserWithEmailAndPassword(auth, invite.email, password);
        } else {
          throw signInError;
        }
      }
      if (auth.currentUser && !auth.currentUser.emailVerified) {
        await sendEmailVerification(auth.currentUser);
        setNeedsVerification(true);
        return;
      }
      await finish();
    });
  };

  if (loadError) {
    return (
      <div className="tenant-invite">
        <h1>This invite is no longer valid</h1>
        <p>It may have been used already, or it may have expired. Ask whoever invited you for a new one.</p>
      </div>
    );
  }

  if (!invite) return <div className="tenant-invite"><p>Loading…</p></div>;

  if (done) {
    return (
      <div className="tenant-invite">
        <h1>You are in</h1>
        <p>You have joined {invite.tenantName}.</p>
        <a className="tenant-invite-cta" href="/tenant/cases">Go to your cases</a>
      </div>
    );
  }

  if (needsVerification) {
    return (
      <div className="tenant-invite">
        <h1>Check your email</h1>
        <p>
          We sent a verification link to {invite.email}. Open it, then come back to this
          page and continue.
        </p>
        <button type="button" className="tenant-invite-cta" disabled={busy} onClick={() => withErrors(finish)}>
          I have verified, continue
        </button>
        {error && <p className="tenant-invite-error" role="alert">{error}</p>}
      </div>
    );
  }

  return (
    <div className="tenant-invite">
      <h1>Join {invite.tenantName}</h1>
      <p className="tenant-invite-sub">
        You were invited as {invite.role === 'admin' ? 'an admin' : 'a member'}, using{' '}
        <strong>{invite.email}</strong>.
      </p>

      {error && <p className="tenant-invite-error" role="alert">{error}</p>}

      {mode === 'choose' ? (
        <div className="tenant-invite-options">
          <button type="button" className="tenant-invite-google" disabled={busy} onClick={joinWithGoogle}>
            Continue with Google
          </button>
          <button type="button" className="tenant-invite-alt" onClick={() => setMode('password')}>
            Use a password instead
          </button>
        </div>
      ) : (
        <form className="tenant-join-form" onSubmit={joinWithPassword}>
          <label>
            <span>Password for {invite.email}</span>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              autoComplete="new-password"
              onChange={e => setPassword(e.target.value)}
            />
          </label>
          <p className="tenant-join-hint">
            If you do not have an account yet, this creates one. Your password is
            handled by Google Firebase and never reaches Coepi.
          </p>
          <button type="submit" className="tenant-invite-cta" disabled={busy}>
            {busy ? 'Joining…' : 'Join'}
          </button>
          <button type="button" className="tenant-invite-alt" onClick={() => setMode('choose')}>
            Back
          </button>
        </form>
      )}
    </div>
  );
};

export default TenantInvitePage;
